import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import './index.css';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import "react-phone-input-2/lib/style.css";
import { setupChunkLoadRecovery } from './utils/chunkLoadRecovery';
import RouteErrorPage from './pages/RouteErrorPage';

// Layout - chargé immédiatement car nécessaire pour toutes les routes
import AppLayout from './layouts/AppLayout';
// Composant de protection - chargé immédiatement pour la vérification d'accès
import RequireActiveSubscription from './components/RequireActiveSubscription';

setupChunkLoadRecovery();

// Composant de chargement pour les routes lazy
const RouteLoader = () => (
  <div className="flex items-center justify-center min-h-dvh bg-white">
    <div className="text-center">
      <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
      <p className="text-gray-600">Chargement...</p>
    </div>
  </div>
);

// Lazy loading de toutes les pages pour améliorer les performances
const App = lazy(() => import('./App'));
const AchatPage = lazy(() => import('./pages/BuyOffers'));
const PropertyDetail = lazy(() => import('./pages/PropertyDetail'));
const SellerSignup = lazy(() => import('./pages/SellerSignUp'));
const Subscriptions = lazy(() => import('./pages/Subscriptions'));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'));
const AddListing = lazy(() => import('./pages/AddListing'));
const MyListings = lazy(() => import('./pages/MesAnnonces'));
const TenantSignup = lazy(() => import('./pages/TenantSignUp'));
const RegisterHub = lazy(() => import('./pages/RegisterHub'));
const ActivationPayment = lazy(() => import('./pages/ActivationPayment'));
const RentOffers = lazy(() => import('./pages/RentOffers'));
const AddRental = lazy(() => import('./pages/AddRental'));
const MyRentListings = lazy(() => import('./pages/MyRentListings'));
const Profile = lazy(() => import('./pages/Profile'));
const Payment = lazy(() => import('./pages/Payment'));
const Login = lazy(() => import('./pages/Login'));
const NotFound = lazy(() => import('./pages/NotFound'));
const EditListing = lazy(() => import('./pages/EditListing'));
const ListingDetail = lazy(() => import('./pages/ListingDetails'));
const RentalManagement = lazy(() => import('./pages/RentalManagment'));
const TenantDetail = lazy(() => import('./pages/TenantDetails'));
const TenantPublicPortal = lazy(() => import('./pages/TenantPublicPortal'));
const Politique = lazy(() => import('./pages/PolitiqueConfidentialite'));
const CGU = lazy(() => import('./pages/cgu'));
const CGV = lazy(() => import('./pages/CGV'));
const Apropos = lazy(() => import('./pages/Apropos'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ContactPage = lazy(() => import('./pages/ContactPage_2s-pay'));
const RequireOwnerConsent = lazy(() => import('./components/RequireOwnerConsent'));
const Messagerie = lazy(() => import('./pages/Messagerie'));
const AgentMessagerie = lazy(() => import('./pages/AgentMessagerie'));
const PublicationMarketing = lazy(() => import('./pages/PublicationMarketing'));

// Wrapper pour les composants lazy avec Suspense
const LazyWrapper = ({ children }) => (
  <Suspense fallback={<RouteLoader />}>
    {children}
  </Suspense>
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { 
        index: true, 
        element: (
          <LazyWrapper>
            <App />
          </LazyWrapper>
        )
      },
      { 
        path: "achat", 
        element: (
          <LazyWrapper>
            <AchatPage />
          </LazyWrapper>
        )
      },
      { 
        path: "propriete/:id", 
        element: (
          <LazyWrapper>
            <PropertyDetail />
          </LazyWrapper>
        )
      },
      { 
        path: "inscription-vendeur", 
        element: (
          <LazyWrapper>
            <SellerSignup role="vendeur" />
          </LazyWrapper>
        )
      },
      { 
        path: "inscription-proprietaire", 
        element: (
          <LazyWrapper>
            <SellerSignup role="proprietaire" />
          </LazyWrapper>
        )
      },
      { 
        path: "inscription-gestionnaire", 
        element: (
          <LazyWrapper>
            <SellerSignup role="manager" />
          </LazyWrapper>
        )
      },
      { 
        path: "abonnements", 
        element: (
          <LazyWrapper>
            <Subscriptions />
          </LazyWrapper>
        )
      },
      {
        path: "abonnement/succes",
        element: (
          <LazyWrapper>
            <SubscriptionSuccess />
          </LazyWrapper>
        )
      },
      { 
        path: "ajouter", 
        element: (
          <LazyWrapper>
            <RequireActiveSubscription>
              <AddListing />
            </RequireActiveSubscription>
          </LazyWrapper>
        )
      },
      { 
        path: "mes-annonces", 
        element: (
          <LazyWrapper>
            <MyListings />
          </LazyWrapper>
        )
      },
      { 
        path: "inscription-locataire", 
        element: (
          <LazyWrapper>
            <TenantSignup />
          </LazyWrapper>
        )
      },
      {
        path: "register",
        element: (
          <LazyWrapper>
            <RegisterHub />
          </LazyWrapper>
        )
      },
      { 
        path: "paiement", 
        element: (
          <LazyWrapper>
            <ActivationPayment />
          </LazyWrapper>
        )
      },
      { 
        path: "location", 
        element: (
          <LazyWrapper>
            <RentOffers />
          </LazyWrapper>
        )
      },
      { 
        path: "listing/:id", 
        element: (
          <LazyWrapper>
            <ListingDetail />
          </LazyWrapper>
        )
      },
      { 
        path: "ajouter-location", 
        element: (
          <LazyWrapper>
            <AddRental />
          </LazyWrapper>
        )
      },
      { 
        path: "mes-annonces-location", 
        element: (
          <LazyWrapper>
            <MyRentListings />
          </LazyWrapper>
        )
      },
      { 
        path: "profil", 
        element: (
          <LazyWrapper>
            <Profile />
          </LazyWrapper>
        )
      },
      { 
        path: "paiement2", 
        element: (
          <LazyWrapper>
            <Payment />
          </LazyWrapper>
        )
      },
      { 
        path: "annonces/:id/edit", 
        element: (
          <LazyWrapper>
            <EditListing />
          </LazyWrapper>
        )
      },
      {
        path: "gestion-locative",
        element: (
          <LazyWrapper>
            <RequireOwnerConsent>
              <RentalManagement />
            </RequireOwnerConsent>
          </LazyWrapper>
        ),
      },
      {
        path: "publication-marketing",
        element: (
          <LazyWrapper>
            <PublicationMarketing />
          </LazyWrapper>
        ),
      },
      {
        path: "locataires/:id",
        element: (
          <LazyWrapper>
            <RequireOwnerConsent>
              <TenantDetail />
            </RequireOwnerConsent>
          </LazyWrapper>
        ),
      },
      { 
        path: "payer-loyer/:token", 
        element: (
          <LazyWrapper>
            <TenantPublicPortal />
          </LazyWrapper>
        )
      },
      { 
        path: "politique-confidentialite", 
        element: (
          <LazyWrapper>
            <Politique />
          </LazyWrapper>
        )
      },
      { 
        path: "cgu", 
        element: (
          <LazyWrapper>
            <CGU />
          </LazyWrapper>
        )
      },
      { 
        path: "cgv", 
        element: (
          <LazyWrapper>
            <CGV />
          </LazyWrapper>
        )
      },
      { 
        path: "apropos", 
        element: (
          <LazyWrapper>
            <Apropos />
          </LazyWrapper>
        )
      },
      { 
        path: "contact", 
        element: (
          <LazyWrapper>
            <ContactPage />
          </LazyWrapper>
        )
      },
      { 
        path: "messagerie", 
        element: (
          <LazyWrapper>
            <Messagerie />
          </LazyWrapper>
        )
      },
      { 
        path: "agent-com/messagerie", 
        element: (
          <LazyWrapper>
            <AgentMessagerie />
          </LazyWrapper>
        )
      },
      // catch-all 404 pour tout ce qui est sous AppLayout
      { 
        path: "*", 
        element: (
          <LazyWrapper>
            <NotFound />
          </LazyWrapper>
        )
      },
    ],
  },
  // route hors layout (ex: connexion)
  { 
    path: "/connexion", 
    element: (
      <LazyWrapper>
        <Login />
      </LazyWrapper>
    ),
    errorElement: <RouteErrorPage />,
  },
  { 
    path: "/mot-de-passe-oublie", 
    element: (
      <LazyWrapper>
        <ForgotPassword />
      </LazyWrapper>
    ),
    errorElement: <RouteErrorPage />,
  },
  { 
    path: "/reset-password", 
    element: (
      <LazyWrapper>
        <ResetPassword />
      </LazyWrapper>
    ),
    errorElement: <RouteErrorPage />,
  },
  // catch-all global (si jamais)
  { 
    path: "*", 
    element: (
      <LazyWrapper>
        <NotFound />
      </LazyWrapper>
    ),
    errorElement: <RouteErrorPage />,
  },
]);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<RouterProvider router={router} />);

serviceWorkerRegistration.unregister();
reportWebVitals();
